import { holdSubjectOf, isHoldTaskType } from '../../domain/constants/holdTaskCatalog';
import { HOLD_SUBJECT } from '../../domain/constants/holdTaskTypes';
import HoldFixCard from '../components/HoldFixCard.vue';
import { presentHoldTask } from '../presenters/holdTaskPresenter';
import { useHoldSubjectNames } from './useHoldSubjectNames';

/** The person whose picture a task about someone shows. */
const itself = (subject) => (hold) => ({ kind: subject, id: hold.subjectId });

/**
 * What each kind of subject looks like in the worklist: its icon, and the person whose picture
 * leads instead when the host has one. A session shows its client. `narrows` says billing items can
 * be narrowed to it: the clinic's charge master holds every item, so its link narrows to none.
 */
const SUBJECTS = {
  [HOLD_SUBJECT.CLIENT]: { icon: 'person', person: itself(HOLD_SUBJECT.CLIENT), narrows: true },
  [HOLD_SUBJECT.STAFF]: { icon: 'badge', person: itself(HOLD_SUBJECT.STAFF), narrows: true },
  [HOLD_SUBJECT.SESSION]: {
    icon: 'event_note', person: (hold) => ({ kind: HOLD_SUBJECT.CLIENT, id: hold.clientId }), narrows: true,
  },
  [HOLD_SUBJECT.SESSION_PLAN]: {
    icon: 'assignment', person: (hold) => ({ kind: HOLD_SUBJECT.CLIENT, id: hold.clientId }), narrows: true,
  },
  [HOLD_SUBJECT.PAYER]: { icon: 'account_balance', narrows: true },
  [HOLD_SUBJECT.TREATMENT_SERVICE]: { icon: 'medical_services', narrows: true },
  [HOLD_SUBJECT.CODE]: { icon: 'request_quote', narrows: true },
  [HOLD_SUBJECT.CLINIC]: { icon: 'request_quote', narrows: false },
};

/**
 * How the worklist shows this context's hold tasks: which it owns, how one reads, and the card that
 * fixes it, all in this context's own terms. The host translates Inbox's task into a hold and
 * supplies what only it knows: the route to billing items, the editors, people's pictures, and the
 * names of session plans, which billing does not own.
 *
 * @param {{
 *   fixSurfaces: import('../../application/ports/FixSurfaces.port').FixSurfaces,
 *   billingItemsLinkFor: (narrowTo: {kind: string, id: string}|null, blockedBy: string) => (string|Object),
 *   pictureOf: (kind: string, id: string) => (string|null),
 *   readSessionPlanName: (sessionPlanId: string) => Promise<string|null>,
 * }} host
 */
// eslint-disable-next-line import/prefer-default-export
export function useHoldTaskProvider({
  fixSurfaces, billingItemsLinkFor, pictureOf, readSessionPlanName,
}) {
  const names = useHoldSubjectNames({ readSessionPlanName });

  return {
    owns: (hold) => isHoldTaskType(hold.holdType),
    describe: (hold) => {
      const subject = holdSubjectOf(hold.holdType);
      const { icon = null, person = null } = SUBJECTS[subject] ?? {};
      const whose = person?.(hold);

      return {
        ...presentHoldTask(hold, names.nameOf(subject, hold.subjectId, hold.clientId ?? null)),
        icon,
        avatarUrl: whose?.id ? pictureOf(whose.kind, whose.id) : null,
      };
    },
    card: HoldFixCard,
    cardProps: (hold) => {
      const subject = holdSubjectOf(hold.holdType);
      const narrowTo = SUBJECTS[subject]?.narrows && hold.subjectId ? { kind: subject, id: hold.subjectId } : null;

      return { hold, fixSurfaces, itemsLink: billingItemsLinkFor(narrowTo, hold.holdType) };
    },
  };
} // useHoldTaskProvider()
